import React, { useState, useEffect, useRef } from 'react';
import { PenTool, Hammer, Gem, ShieldCheck, ArrowRight, Clock, Ruler, Microscope, CheckCircle2, Star, Sparkles } from 'lucide-react';

interface ServicesProps {
  onNavigate: (page: string) => void;
}

const servicesList = [
  {
    id: 'bespoke',
    title: 'Bespoke Commissions',
    icon: <PenTool size={20} />,
    description: 'Collaborate with our master artisans to bring your dream design to life. From initial sketch to 3D modeling and final setting, every step is a personal journey crafted around your vision.',
    image: 'https://images.unsplash.com/photo-1531995811006-35cb42e1a022?q=80&w=2070&auto=format&fit=crop',
    details: ['Personal Consultation', '3D CAD Rendering', 'Hand-Forged Settings']
  },
  {
    id: 'sourcing',
    title: 'Rare Gem Sourcing',
    icon: <Gem size={20} />,
    description: 'Our global network allows us to acquire the rarest diamonds and colored gemstones. We verify provenance and quality, ensuring your investment is ethically sourced and physically brilliant.',
    image: 'https://images.unsplash.com/photo-1600607686527-6fb886090705?q=80&w=2725&auto=format&fit=crop',
    details: ['GIA/AGS Certified', 'Conflict-Free Origins', 'Investment Grade Options']
  },
  {
  id: 'restoration',
  title: 'Heirloom Restoration',
  icon: <Hammer size={20} />,
  description: 'Preserve the legacy of your family treasures. Our restoration experts meticulously breathe new life into vintage pieces while respecting their original integrity and patina.',
  image: 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?auto=format&fit=crop&w=2070&q=80',
  details: [
    'Stone Polishing',
    'Prong Retipping',
    'Metal Refinishing'
  ]
},
  {
    id: 'valuation',
    title: 'Appraisal & Valuation',
    icon: <ShieldCheck size={20} />,
    description: 'Receive accurate, market-reflective valuations for insurance or estate purposes. Our documentation is recognized by major insurers worldwide.',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070&auto=format&fit=crop',
    details: ['Insurance Replacement', 'Estate Valuation', 'Liquidation Estimates']
  }
];

const materials = [
  {
    id: 'yellow-gold',
    name: '18k Yellow Gold',
    desc: 'The classic choice. Rich, warm, and timeless, our 18k yellow gold is alloyed for optimal durability while maintaining that signature luster.',
    image: 'https://images.unsplash.com/photo-1601121141461-9d6647bca1ed?q=80&w=2000&auto=format&fit=crop'
  },
  {
    id: 'platinum',
    name: '950 Platinum',
    desc: 'The ultimate expression of purity and strength. Naturally white and hypoallergenic, platinum develops a desirable patina over time.',
    image: 'https://images.unsplash.com/photo-1573408301185-9146fe634ad0?q=80&w=2000&auto=format&fit=crop'
  },
  {
    id: 'rose-gold',
    name: '18k Rose Gold',
    desc: 'A romantic blend of gold and copper alloy. Its blush tone complements all skin types and adds a vintage-inspired warmth to modern designs.',
    image: 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?q=80&w=2000&auto=format&fit=crop'
  }
];

const portfolioImages = [
  { src: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?q=80&w=2070&auto=format&fit=crop', title: 'The Aurora Ring', cat: 'Bespoke' },
  { src: 'https://images.unsplash.com/photo-1615655406736-b37c4fabf923?q=80&w=2070&auto=format&fit=crop', title: 'Midnight Sapphire', cat: 'Sourcing' },
  { src: 'https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?q=80&w=2070&auto=format&fit=crop', title: 'Vintage Restoration', cat: 'Restoration' },
  { src: 'https://images.unsplash.com/photo-1599643477877-530eb83abc8e?q=80&w=2070&auto=format&fit=crop', title: 'Custom Pendant', cat: 'Bespoke' },
  { src: 'https://images.unsplash.com/photo-1602173574767-37ac01994b2a?q=80&w=2070&auto=format&fit=crop', title: 'Golden Legacy', cat: 'Heirloom' },
];

const processSteps = [
  { step: '01', title: 'Consultation', desc: 'We discuss your inspiration, budget, and timeline.', icon: <Clock size={20} /> },
  { step: '02', title: 'Design', desc: 'Sketches and 3D renderings are presented for approval.', icon: <Ruler size={20} /> },
  { step: '03', title: 'Sourcing', desc: 'We select the perfect gemstones to match your criteria.', icon: <Gem size={20} /> },
  { step: '04', title: 'Crafting', desc: 'Our master bench jewelers hand-fabricate your piece.', icon: <Hammer size={20} /> },
  { step: '05', title: 'Reveal', desc: 'Final inspection and a celebratory presentation.', icon: <Microscope size={20} /> },
];

const Services: React.FC<ServicesProps> = ({ onNavigate }) => {
  const [activeService, setActiveService] = useState(servicesList[0]);
  const [activeMaterial, setActiveMaterial] = useState(materials[0]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const displayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleServiceChange = (service: typeof servicesList[0]) => {
    if (service.id === activeService.id) return;
    setIsAnimating(true);
    setTimeout(() => {
      setActiveService(service);
      setIsAnimating(false);
    }, 400);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!displayRef.current) return;
    const { left, top, width, height } = displayRef.current.getBoundingClientRect();
    const x = (e.clientX - left) / width;
    const y = (e.clientY - top) / height;
    setMousePos({ x, y });
  };

  return (
    <div className="bg-stone-50 min-h-screen">
      
      {/* 1. CINEMATIC HERO SECTION */}
      <section className="relative h-screen w-full overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 z-0">
           {/* Zoom Effect Image */}
           <div className="w-full h-full animate-[zoomSlow_30s_infinite_alternate] transform origin-center">
             <img 
               src="https://images.unsplash.com/photo-1617038260897-41a1f14a8ca0?q=80&w=2070&auto=format&fit=crop" 
               alt="Luxury Jewelry Background"
               className="w-full h-full object-cover grayscale-[20%] opacity-100"
             />
           </div>
           {/* Enhanced Overlay for Readability */}
           <div className="absolute inset-0 bg-stone-900/60 mix-blend-multiply"></div>
           <div className="absolute inset-0 bg-black/40"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl">
          <div className="mb-6 flex justify-center items-center gap-4 text-white/80 animate-fade-in">
             <span className="h-[1px] w-12 bg-gold-400"></span>
             <span className="text-xs uppercase tracking-[0.3em] font-medium text-gold-300">Est. 1985</span>
             <span className="h-[1px] w-12 bg-gold-400"></span>
          </div>
          <h1 className="font-serif text-6xl md:text-8xl text-white mb-8 leading-[1.1] drop-shadow-2xl animate-fade-in">
            The Art of the <br/><span className="italic text-gold-300">Exceptional</span>
          </h1>
          <p className="text-stone-100 text-lg font-light max-w-xl mx-auto leading-relaxed mb-10 drop-shadow-xl animate-fade-in delay-200">
            We do not just make jewelry; we craft legacies. Experience the pinnacle of bespoke design and master craftsmanship in Manila.
          </p>
          <div className="animate-bounce absolute bottom-10 left-1/2 -translate-x-1/2 text-white/50">
            <Clock size={24} strokeWidth={1} />
          </div>
        </div>
      </section>

      {/* 2. INTRODUCTORY STATEMENT */}
      <section className="py-28 px-6 w-full text-center relative overflow-hidden">
        {/* Background Photo with Light Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1589127827299-6e3212871625?q=80&w=2070&auto=format&fit=crop"
            alt="Artisan Texture Background" 
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-stone-50/80 backdrop-blur-[2px]"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-stone-50 via-transparent to-stone-50 opacity-80"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto">
            <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-[1px] h-20 bg-stone-400"></div>

            <p className="font-serif text-3xl md:text-5xl text-stone-900 leading-snug max-w-5xl mx-auto mt-6 font-medium drop-shadow-sm">
            "True luxury is personal. It is the silent language of rare gems, the weight of gold, and the story woven into every setting."
            </p>
            <div className="flex justify-center mt-12">
            </div>
        </div>
      </section>

      {/* 3. INTERACTIVE SERVICES TABS */}
      <section className="max-w-[1440px] mx-auto px-6 mb-32" id="services-tabs">
        <div className="flex flex-col lg:flex-row gap-0 bg-white shadow-2xl shadow-stone-200/50 overflow-hidden min-h-[650px] border border-stone-100">
          
          {/* Left: Navigation Panel */}
          <div className="lg:w-1/3 bg-stone-50 p-8 md:p-12 border-r border-stone-200 flex flex-col justify-center relative z-20">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gold-300 to-transparent"></div>
            <h3 className="font-serif text-3xl mb-2 text-stone-900">Our Expertise</h3>
            <p className="text-xs text-stone-500 uppercase tracking-widest mb-10">Select a service to explore</p>
            
            <div className="space-y-1">
              {servicesList.map((service) => (
                <button
                  key={service.id}
                  onClick={() => handleServiceChange(service)}
                  className={`w-full text-left px-6 py-6 flex items-center justify-between transition-all duration-300 group border-b border-stone-200 hover:bg-white ${
                    activeService.id === service.id 
                      ? 'bg-white border-l-4 border-l-gold-600 shadow-sm' 
                      : 'border-l-4 border-l-transparent text-stone-400'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className={`transition-colors duration-300 ${activeService.id === service.id ? 'text-gold-600' : 'text-stone-400 group-hover:text-gold-500'}`}>
                      {service.icon}
                    </span>
                    <span className={`text-sm uppercase tracking-widest font-medium transition-colors ${activeService.id === service.id ? 'text-stone-900' : 'text-stone-500'}`}>
                      {service.title}
                    </span>
                  </div>
                  {activeService.id === service.id && <ArrowRight size={16} className="text-gold-600 animate-pulse" />}
                </button>
              ))}
            </div>
          </div>

          {/* Right: Immersive Content Display */}
          <div 
            ref={displayRef}
            onMouseMove={handleMouseMove}
            className="lg:w-2/3 relative bg-stone-900 overflow-hidden group cursor-crosshair"
          >
            {/* Parallax Background Image */}
            <div className="absolute inset-0 z-0 overflow-hidden">
               <img 
                 src={activeService.image} 
                 alt={activeService.title}
                 style={{
                   transform: `scale(1.1) translate(${(mousePos.x - 0.5) * -20}px, ${(mousePos.y - 0.5) * -20}px)`
                 }}
                 className={`w-full h-full object-cover transition-all duration-700 ease-out ${
                   isAnimating ? 'opacity-50 blur-sm scale-100' : 'opacity-100 blur-0'
                 }`}
               />
               {/* Refined gradient overlay for text readability - Lightened for better visibility of background image */}
               <div className="absolute inset-0 bg-gradient-to-r from-stone-900/80 via-stone-900/30 to-transparent"></div>
               
               {/* Decorative noise texture */}
               <div className="absolute inset-0 bg-noise opacity-20 mix-blend-overlay"></div>
            </div>

            {/* Floating particles/elements */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
               <div className="absolute top-10 right-10 opacity-20 animate-pulse text-gold-200">
                  <Sparkles size={48} strokeWidth={1} />
               </div>
            </div>

            {/* Content Overlay */}
            <div className={`relative z-10 h-full flex flex-col justify-end p-8 md:p-16 text-white transition-all duration-500 ${isAnimating ? 'opacity-0 translate-y-8' : 'opacity-100 translate-y-0'}`}>
              <div className="mb-6 inline-flex items-center gap-2 border border-white/30 px-3 py-1 rounded-full backdrop-blur-sm bg-white/10 w-fit">
                <Star size={12} className="text-gold-400" fill="currentColor" />
                <span className="text-[10px] uppercase tracking-widest font-bold">Premium Service</span>
              </div>
              
              <h2 className="font-serif text-5xl md:text-6xl mb-6 leading-tight drop-shadow-lg">{activeService.title}</h2>
              <p className="font-light text-stone-200 text-lg leading-relaxed max-w-xl mb-10 border-l border-gold-500 pl-6 drop-shadow-md">
                {activeService.description}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {activeService.details.map((detail, idx) => (
                   <div key={idx} className="flex items-center gap-3 group/item transition-transform hover:translate-x-1">
                      <CheckCircle2 size={18} className="text-gold-500 group-hover/item:scale-110 transition-transform" />
                      <span className="text-xs tracking-wider uppercase text-stone-300 group-hover/item:text-white transition-colors font-medium">
                        {detail}
                      </span>
                   </div>
                ))}
              </div>

              <button 
                onClick={() => onNavigate('contact')}
                className="group w-fit bg-white/10 backdrop-blur-md border border-white/40 text-white px-8 py-4 uppercase text-xs tracking-[0.2em] hover:bg-gold-600 hover:border-gold-600 hover:text-white transition-all duration-300 flex items-center gap-3 shadow-lg"
              >
                Start Consultation 
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 4. INTERACTIVE MATERIAL LIBRARY */}
      <section className="bg-stone-100 py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-noise opacity-30 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
           <div className="text-center mb-16">
              <span className="text-gold-600 text-xs tracking-[0.3em] uppercase font-bold">The Foundation</span>
              <h2 className="font-serif text-4xl md:text-5xl text-stone-900 mt-3 mb-6">Curate Your Palette</h2>
              <p className="text-stone-500 max-w-2xl mx-auto font-light">
                Every bespoke piece begins with the choice of metal. Select a material below to discover its unique character and properties.
              </p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {materials.map((mat) => (
                <div 
                  key={mat.id}
                  onClick={() => setActiveMaterial(mat)}
                  className={`cursor-pointer group relative h-96 transition-all duration-500 border border-stone-200 bg-white shadow-lg overflow-hidden hover:-translate-y-1 hover:shadow-xl ${activeMaterial.id === mat.id ? 'ring-1 ring-gold-500' : ''}`}
                >
                  <div className="absolute inset-0 bg-black">
                    <img 
                      src={mat.image} 
                      alt={mat.name} 
                      className={`w-full h-full object-cover transition-opacity duration-500 ${activeMaterial.id === mat.id ? 'opacity-70 scale-105' : 'opacity-60 group-hover:opacity-70 scale-100'}`}
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent p-8 flex flex-col justify-end">
                     <div className={`w-3 h-3 rounded-full mb-4 ${
                       mat.id === 'yellow-gold' ? 'bg-[#FFD700]' : 
                       mat.id === 'platinum' ? 'bg-[#E5E4E2]' : 'bg-[#B76E79]'
                     }`}></div>
                     
                     {/* Title - Fixed height wrapper to maintain alignment */}
                     <div className="min-h-[3rem] flex items-end">
                         <h3 className={`font-serif text-2xl text-white transition-colors duration-300 ${activeMaterial.id === mat.id ? 'text-gold-200' : ''}`}>
                           {mat.name}
                         </h3>
                     </div>

                     {/* Description - Animated reveal */}
                     <div className={`overflow-hidden transition-all duration-500 ease-in-out ${activeMaterial.id === mat.id ? 'max-h-40 opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
                        <p className="text-stone-300 text-sm leading-relaxed font-light border-t border-white/20 pt-4">
                          {mat.desc}
                        </p>
                     </div>
                     
                     {/* Hover Hint */}
                     {activeMaterial.id !== mat.id && (
                       <span className="text-xs text-white/50 uppercase tracking-widest mt-4 opacity-0 group-hover:opacity-100 transition-opacity absolute bottom-8">Click to Reveal</span>
                     )}
                  </div>
                </div>
              ))}
           </div>
        </div>
      </section>

      {/* 5. PORTFOLIO GALLERY (MASONRY STYLE) */}
      <section className="py-32 bg-stone-50 relative">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
           <div className="flex flex-col md:flex-row justify-between items-end mb-16 border-b border-stone-200 pb-8">
              <div>
                <span className="text-stone-400 text-xs tracking-[0.3em] uppercase font-bold">Selected Works</span>
                <h2 className="font-serif text-4xl text-stone-900 mt-2">From The Vault</h2>
              </div>
              <button 
                onClick={() => onNavigate('shop')}
                className="hidden md:flex items-center gap-2 text-xs uppercase tracking-widest text-stone-900 hover:text-gold-600 transition-colors"
              >
                 View Full Portfolio <ArrowRight size={14} />
              </button>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 h-[600px] md:h-[500px]">
              {portfolioImages.map((img, idx) => (
                <div 
                  key={idx} 
                  className={`relative group overflow-hidden cursor-pointer ${idx === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
                >
                  <img 
                    src={img.src} 
                    alt={img.title} 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center text-center p-4">
                     <span className="text-gold-400 text-[10px] uppercase tracking-widest mb-2 translate-y-4 group-hover:translate-y-0 transition-transform duration-300 delay-75">
                       {img.cat}
                     </span>
                     <h4 className="text-white font-serif text-2xl translate-y-4 group-hover:translate-y-0 transition-transform duration-300 delay-100">
                       {img.title}
                     </h4>
                  </div>
                </div>
              ))}
           </div>
        </div>
      </section>

      {/* 6. PROCESS TIMELINE */}
      <section className="bg-stone-900 text-white py-24 relative overflow-hidden">
         {/* Decorative Background */}
         <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-gold-600 via-stone-900 to-stone-900"></div>
         </div>

         <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="text-center mb-16">
              <span className="text-gold-500 text-xs tracking-[0.3em] uppercase font-bold">The Journey</span>
              <h2 className="font-serif text-3xl md:text-4xl mt-3">From Concept to Creation</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
               {processSteps.map((step, idx) => (
                 <div key={idx} className="relative flex flex-col items-center text-center group">
                    {/* Connecting Line (Desktop) */}
                    {idx !== processSteps.length - 1 && (
                      <div className="hidden md:block absolute top-6 left-1/2 w-full h-[1px] bg-stone-800 -z-10">
                         <div className="w-0 h-full bg-gold-600 transition-all duration-700 delay-100 group-hover:w-full"></div>
                      </div>
                    )}

                    <div className="w-12 h-12 rounded-full bg-stone-800 border border-stone-700 flex items-center justify-center text-gold-500 mb-6 group-hover:bg-gold-600 group-hover:text-white group-hover:scale-110 transition-all duration-300 shadow-xl z-10 relative">
                       {step.icon}
                    </div>
                    
                    <span className="text-stone-500 text-[10px] font-bold tracking-widest mb-2">STEP {step.step}</span>
                    <h4 className="font-serif text-xl mb-3 group-hover:text-gold-400 transition-colors">{step.title}</h4>
                    <p className="text-xs text-stone-400 leading-relaxed max-w-[150px]">
                      {step.desc}
                    </p>
                 </div>
               ))}
            </div>
         </div>
      </section>

      {/* 7. CTA SECTION */}
      <section className="py-24 max-w-5xl mx-auto px-6 text-center">
         <div className="p-12 border border-gold-200 bg-white relative shadow-lg shadow-stone-200/50">
            <div className="absolute top-0 left-0 w-2 h-2 bg-gold-500"></div>
            <div className="absolute top-0 right-0 w-2 h-2 bg-gold-500"></div>
            <div className="absolute bottom-0 left-0 w-2 h-2 bg-gold-500"></div>
            <div className="absolute bottom-0 right-0 w-2 h-2 bg-gold-500"></div>

            <h2 className="font-serif text-4xl text-stone-900 mb-6">Ready to Begin?</h2>
            <p className="text-stone-600 font-light mb-8 max-w-lg mx-auto">
               Our gemologists and master jewelers are ready to guide you through our collections or start your bespoke commission.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-6">
               <button 
                onClick={() => onNavigate('contact')}
                className="bg-stone-900 text-white px-8 py-4 text-xs tracking-[0.2em] uppercase hover:bg-gold-600 transition-colors shadow-lg"
               >
                  Book an Appointment
               </button>
               <button 
                onClick={() => onNavigate('contact')}
                className="border border-stone-900 text-stone-900 px-8 py-4 text-xs tracking-[0.2em] uppercase hover:bg-stone-50 transition-colors"
               >
                  Contact Concierge
               </button>
            </div>
         </div>
      </section>

    </div>
  );
};

export default Services;